import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ComponentLibrary from '../components/ComponentLibrary';
import ThreeCanvas from '../components/ThreeCanvas';
import SummaryPanel from '../components/SummaryPanel';
import AIAssistant from '../components/AIAssistant';
import AlertSystem from '../components/AlertSystem';
import QuoteGenerator from '../components/QuoteGenerator';
import ConfigurationPicker from '../components/ConfigurationPicker';
import MaterialEditor from '../components/MaterialEditor';
import VoiceAssistant from '../components/VoiceAssistant';
import DesignRender from '../components/DesignRender';
import { VEHICLE_SPECS } from '../data/components';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Layers, Save, Share2, LogOut, FolderOpen,
  Undo2, Redo2, Trash2, MessageCircle, FileText, Settings, LayoutGrid, Paintbrush, Mic, Eye, Grid3X3, Image
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DesignStudio() {
  const { designId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [components, setComponents] = useState([]);
  const [placedComponents, setPlacedComponents] = useState([]);
  const [designName, setDesignName] = useState('Untitled Design');
  const [currentDesignId, setCurrentDesignId] = useState(designId || null);
  const [shareId, setShareId] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showAI, setShowAI] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showConfig, setShowConfig] = useState(!designId); // Show config picker for new designs
  const [showRender, setShowRender] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [moveMode, setMoveMode] = useState(null);
  const [customMaterials, setCustomMaterials] = useState({});
  const [showMaterial, setShowMaterial] = useState(false);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewMode, setViewMode] = useState('3d'); // '2d' or '3d'

  // Check admin role
  useEffect(() => {
    axios.get(`${API}/auth/role`, { withCredentials: true })
      .then(r => setIsAdmin(r.data.is_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  // Fetch component catalog
  useEffect(() => {
    axios.get(`${API}/components`).then(r => setComponents(r.data)).catch(console.error);
  }, []);

  // Load existing design
  useEffect(() => {
    if (designId) {
      axios.get(`${API}/designs/${designId}`, { withCredentials: true })
        .then(r => {
          setPlacedComponents(r.data.placed_components || []);
          setDesignName(r.data.name);
          setCurrentDesignId(r.data.design_id);
          setShareId(r.data.share_id);
          setHistory([r.data.placed_components || []]);
          setHistoryIndex(0);
        })
        .catch(() => toast.error('Failed to load design'));
    }
  }, [designId]);

  // Check alerts whenever placed components change
  const checkAlerts = useCallback(async (comps) => {
    try {
      const r = await axios.post(`${API}/alerts/check`, { placed_components: comps });
      setAlerts(r.data.alerts || []);
      setTotalWeight(r.data.total_weight || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (placedComponents.length > 0) {
      checkAlerts(placedComponents);
      // Calculate total cost
      const cost = placedComponents.reduce((sum, pc) => {
        const comp = components.find(c => c.comp_id === pc.comp_id);
        return sum + (comp ? comp.price * (pc.quantity || 1) : 0);
      }, 0);
      setTotalCost(cost);
    } else {
      setAlerts([]);
      setTotalCost(0);
      setTotalWeight(0);
    }
  }, [placedComponents, components, checkAlerts]);

  // Push to history for undo/redo
  const pushHistory = useCallback((newComps) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newComps);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const handleDrop = useCallback((compId, x, y) => {
    const comp = components.find(c => c.comp_id === compId);
    if (!comp) return;

    // Validate bounds
    if (x + comp.grid_w > VEHICLE_SPECS.gridCols || y + comp.grid_h > VEHICLE_SPECS.gridRows) {
      toast.error('Component out of bounds');
      return;
    }
    if (x < 0 || y < 0) return;

    // Check driver zone
    if (y < VEHICLE_SPECS.driverZone.h) {
      toast.error('Cannot place in driver area');
      return;
    }

    // Check collision with existing
    const collision = placedComponents.some(pc => {
      const ec = components.find(c => c.comp_id === pc.comp_id);
      if (!ec) return false;
      return !(x + comp.grid_w <= pc.x || x >= pc.x + ec.grid_w || y + comp.grid_h <= pc.y || y >= pc.y + ec.grid_h);
    });
    if (collision) {
      toast.error('Space occupied');
      return;
    }

    const newPlaced = [...placedComponents, {
      instance_id: `${compId}_${Date.now()}`,
      comp_id: compId,
      x, y,
      quantity: 1
    }];
    setPlacedComponents(newPlaced);
    pushHistory(newPlaced);
    toast.success(`${comp.name} placed`);
  }, [components, placedComponents, pushHistory]);

  const handleMoveComponent = useCallback((instanceId, newX, newY) => {
    const pc = placedComponents.find(p => p.instance_id === instanceId);
    if (!pc) return;
    const comp = components.find(c => c.comp_id === pc.comp_id);
    if (!comp) return;

    if (newX + comp.grid_w > VEHICLE_SPECS.gridCols || newY + comp.grid_h > VEHICLE_SPECS.gridRows || newX < 0 || newY < 0) return;
    if (newY < VEHICLE_SPECS.driverZone.h) return;

    const collision = placedComponents.some(other => {
      if (other.instance_id === instanceId) return false;
      const ec = components.find(c => c.comp_id === other.comp_id);
      if (!ec) return false;
      return !(newX + comp.grid_w <= other.x || newX >= other.x + ec.grid_w || newY + comp.grid_h <= other.y || newY >= other.y + ec.grid_h);
    });
    if (collision) return;

    const newPlaced = placedComponents.map(p =>
      p.instance_id === instanceId ? { ...p, x: newX, y: newY } : p
    );
    setPlacedComponents(newPlaced);
    pushHistory(newPlaced);
  }, [components, placedComponents, pushHistory]);

  const handleRemoveComponent = useCallback((instanceId) => {
    const newPlaced = placedComponents.filter(p => p.instance_id !== instanceId);
    setPlacedComponents(newPlaced);
    pushHistory(newPlaced);
    toast.info('Component removed');
  }, [placedComponents, pushHistory]);

  // Rotate component 90 degrees
  const handleRotateComponent = useCallback((instanceId) => {
    const newPlaced = placedComponents.map(p => {
      if (p.instance_id !== instanceId) return p;
      const currentRotation = p.rotation || 0;
      const newRotation = (currentRotation + 90) % 360;
      return { ...p, rotation: newRotation };
    });
    setPlacedComponents(newPlaced);
    pushHistory(newPlaced);
    toast.success('Component rotated');
  }, [placedComponents, pushHistory]);

  // Add component by double-clicking in library - finds first valid position
  const handleAddComponent = useCallback((compId) => {
    const comp = components.find(c => c.comp_id === compId);
    if (!comp) return;
    
    // Find first valid position (scan from top-left, avoiding driver zone)
    for (let y = VEHICLE_SPECS.driverZone.h; y <= VEHICLE_SPECS.gridRows - comp.grid_h; y++) {
      for (let x = 0; x <= VEHICLE_SPECS.gridCols - comp.grid_w; x++) {
        // Check collision
        const collision = placedComponents.some(pc => {
          const ec = components.find(c => c.comp_id === pc.comp_id);
          if (!ec) return false;
          return !(x + comp.grid_w <= pc.x || x >= pc.x + ec.grid_w || y + comp.grid_h <= pc.y || y >= pc.y + ec.grid_h);
        });
        
        if (!collision) {
          handleDrop(compId, x, y);
          return;
        }
      }
    }
    toast.error('No space available for this component');
  }, [components, placedComponents, handleDrop]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setPlacedComponents(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setPlacedComponents(history[historyIndex + 1]);
    }
  };

  const handleClearAll = () => {
    setPlacedComponents([]);
    pushHistory([]);
    setSelectedInstance(null);
    setMoveMode(null);
    toast.info('Canvas cleared');
  };

  // Valid placements for move mode
  const validPlacements = useMemo(() => {
    if (!moveMode) return [];
    const pc = placedComponents.find(p => p.instance_id === moveMode);
    if (!pc) return [];
    const comp = components.find(c => c.comp_id === pc.comp_id);
    if (!comp) return [];
    const valid = [];
    for (let y = VEHICLE_SPECS.driverZone.h; y <= VEHICLE_SPECS.gridRows - comp.grid_h; y++) {
      for (let x = 0; x <= VEHICLE_SPECS.gridCols - comp.grid_w; x++) {
        const collision = placedComponents.some(other => {
          if (other.instance_id === moveMode) return false;
          const ec = components.find(c => c.comp_id === other.comp_id);
          if (!ec) return false;
          return !(x + comp.grid_w <= other.x || x >= other.x + ec.grid_w ||
                   y + comp.grid_h <= other.y || y >= other.y + ec.grid_h);
        });
        if (!collision) valid.push({ x, y });
      }
    }
    return valid;
  }, [moveMode, placedComponents, components]);

  // Configuration selection
  const handleSelectConfig = useCallback((config) => {
    const placed = config.components.map((c, i) => ({
      instance_id: `${c.comp_id}_config_${i}_${Date.now()}`,
      comp_id: c.comp_id,
      x: c.x,
      y: c.y,
      quantity: 1,
    }));
    setPlacedComponents(placed);
    pushHistory(placed);
    setDesignName(config.name);
    setShowConfig(false);
    toast.success(`Loaded "${config.name}" configuration`);
  }, [pushHistory]);

  const handleMaterialChange = useCallback((instanceId, materials) => {
    setCustomMaterials(prev => ({ ...prev, [instanceId]: materials }));
  }, []);

  const selectedComp = useMemo(() => {
    if (!selectedInstance) return null;
    const pc = placedComponents.find(p => p.instance_id === selectedInstance);
    if (!pc) return null;
    return components.find(c => c.comp_id === pc.comp_id);
  }, [selectedInstance, placedComponents, components]);

  const handleSave = async () => {
    try {
      const payload = { name: designName, placed_components: placedComponents };
      if (currentDesignId) {
        const r = await axios.put(`${API}/designs/${currentDesignId}`, payload, { withCredentials: true });
        setShareId(r.data.share_id);
        toast.success('Design saved');
      } else {
        const r = await axios.post(`${API}/designs`, payload, { withCredentials: true });
        setCurrentDesignId(r.data.design_id);
        setShareId(r.data.share_id);
        navigate(`/studio/${r.data.design_id}`, { replace: true });
        toast.success('Design created');
      }
      setShowSave(false);
    } catch {
      toast.error('Save failed');
    }
  };

  const handleShare = () => {
    if (shareId) {
      const url = `${window.location.origin}/shared/${shareId}`;
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } else {
      toast.error('Save first to get a share link');
    }
  };

  const designContext = {
    placed_components: placedComponents,
    total_cost: totalCost,
    total_weight: totalWeight,
  };

  return (
    <div className="h-screen flex flex-col bg-[#020617] overflow-hidden" data-testid="design-studio">
      {/* Top Bar */}
      <header className="h-14 glass-panel flex items-center justify-between px-4 z-40 shrink-0" data-testid="studio-header">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <img src="/aa-logo.png" alt="AA" className="h-7" />
            <span className="font-chivo font-bold text-sm text-white">Archana Automotives</span>
          </div>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <span className="text-slate-400 text-sm truncate max-w-48">{designName}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-800/50 rounded-lg p-0.5">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setViewMode('2d')}
              className={`h-7 px-2 text-xs ${viewMode === '2d' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="view-2d-btn"
            >
              <Grid3X3 className="w-3.5 h-3.5 mr-1" />
              2D
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setViewMode('3d')}
              className={`h-7 px-2 text-xs ${viewMode === '3d' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="view-3d-btn"
            >
              <Eye className="w-3.5 h-3.5 mr-1" />
              3D
            </Button>
          </div>
          <div className="w-px h-6 bg-white/10" />
          <Button variant="ghost" size="sm" onClick={() => setShowConfig(true)}
            className="text-slate-400 hover:text-white gap-1.5" data-testid="config-btn">
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Templates</span>
          </Button>
          <div className="w-px h-6 bg-white/10" />
          <Button variant="ghost" size="sm" onClick={handleUndo} disabled={historyIndex === 0}
            className="text-slate-400 hover:text-white" data-testid="undo-btn">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleRedo} disabled={historyIndex >= history.length - 1}
            className="text-slate-400 hover:text-white" data-testid="redo-btn">
            <Redo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleClearAll}
            className="text-slate-400 hover:text-white" data-testid="clear-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
          <div className="w-px h-6 bg-white/10" />
          <Button variant="ghost" size="sm" onClick={() => setShowSave(true)}
            className="text-slate-400 hover:text-white gap-1.5" data-testid="save-btn">
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Save</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleShare}
            className="text-slate-400 hover:text-white gap-1.5" data-testid="share-btn">
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Share</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowQuote(true)}
            className="text-slate-400 hover:text-white gap-1.5" data-testid="quote-btn">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Quote</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowRender(true)}
            className="text-slate-400 hover:text-white gap-1.5" data-testid="render-btn"
            disabled={placedComponents.length === 0}>
            <Image className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Render</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/designs')}
            className="text-slate-400 hover:text-white gap-1.5" data-testid="my-designs-btn">
            <FolderOpen className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Designs</span>
          </Button>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}
              className="text-amber-400 hover:text-amber-300 gap-1.5" data-testid="admin-btn">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Admin</span>
            </Button>
          )}
          <div className="w-px h-6 bg-white/10" />
          <div className="flex items-center gap-2">
            {user?.picture && (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-white/10" />
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left - Component Library */}
        <aside className="w-72 border-r border-white/5 bg-slate-900/40 flex flex-col overflow-hidden shrink-0" data-testid="component-library-panel">
          <ComponentLibrary components={components} onAddComponent={handleAddComponent} />
        </aside>

        {/* Center - 3D Canvas */}
        <main className="flex-1 flex flex-col overflow-hidden relative" data-testid="canvas-area">
          {/* Alerts */}
          {alerts.length > 0 && (
            <AlertSystem alerts={alerts} />
          )}

          {/* Move mode indicator */}
          {moveMode && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 bg-blue-600 text-white px-4 py-2 rounded-full text-xs font-medium shadow-lg">
              Click a green zone to move. Press Escape to cancel.
            </div>
          )}

          <ThreeCanvas
            components={components}
            placedComponents={placedComponents}
            onDrop={handleDrop}
            onMove={handleMoveComponent}
            onRemove={handleRemoveComponent}
            selectedInstance={selectedInstance}
            setSelectedInstance={setSelectedInstance}
            moveMode={moveMode}
            setMoveMode={setMoveMode}
            validPlacements={validPlacements}
            customMaterials={customMaterials}
            viewMode={viewMode}
          />
        </main>

        {/* Right - Summary Panel + Material Editor */}
        <aside className="w-80 border-l border-white/5 bg-slate-900/40 flex flex-col overflow-hidden shrink-0" data-testid="summary-panel">
          {/* Material Editor (when component selected) */}
          {selectedInstance && selectedComp && (
            <MaterialEditor
              selectedInstance={selectedInstance}
              comp={selectedComp}
              customMaterials={customMaterials}
              onMaterialChange={handleMaterialChange}
              onClose={() => setSelectedInstance(null)}
              onMove={(id) => setMoveMode(id)}
              onRotate={handleRotateComponent}
              onRemove={(id) => { handleRemoveComponent(id); setSelectedInstance(null); }}
              placedComponent={placedComponents.find(p => p.instance_id === selectedInstance)}
            />
          )}

          <SummaryPanel
            components={components}
            placedComponents={placedComponents}
            totalCost={totalCost}
            totalWeight={totalWeight}
            onRemove={handleRemoveComponent}
          />
        </aside>
      </div>

      {/* Voice Assistant Toggle */}
      <button
        data-testid="voice-toggle-btn"
        onClick={() => setShowVoice(!showVoice)}
        className="fixed bottom-6 right-[26rem] z-50 w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
      >
        <Mic className="w-5 h-5 text-white" />
      </button>

      {/* AI Assistant Toggle */}
      <button
        data-testid="ai-toggle-btn"
        onClick={() => setShowAI(!showAI)}
        className="fixed bottom-6 right-96 z-50 w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
      >
        <MessageCircle className="w-5 h-5 text-white" />
      </button>

      {/* Voice Assistant Panel */}
      {showVoice && (
        <VoiceAssistant
          designContext={designContext}
          onClose={() => setShowVoice(false)}
        />
      )}

      {/* AI Assistant Panel */}
      {showAI && (
        <AIAssistant
          designContext={designContext}
          onClose={() => setShowAI(false)}
        />
      )}

      {/* Save Dialog */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent className="bg-slate-900 border-white/10 text-white" data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Save Design</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="design-name-input"
              value={designName}
              onChange={e => setDesignName(e.target.value)}
              placeholder="Design name..."
              className="bg-slate-800 border-white/10 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSave(false)} className="text-slate-400" data-testid="save-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-confirm-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Generator */}
      {showQuote && (
        <QuoteGenerator
          components={components}
          placedComponents={placedComponents}
          totalCost={totalCost}
          totalWeight={totalWeight}
          designName={designName}
          user={user}
          onClose={() => setShowQuote(false)}
        />
      )}

      {/* Configuration Picker */}
      {showConfig && (
        <ConfigurationPicker
          onSelectConfig={handleSelectConfig}
          onStartFresh={() => setShowConfig(false)}
          onClose={() => setShowConfig(false)}
        />
      )}

      {/* Design Render View */}
      {showRender && (
        <DesignRender
          components={components}
          placedComponents={placedComponents}
          customMaterials={customMaterials}
          designName={designName}
          totalCost={totalCost}
          totalWeight={totalWeight}
          onClose={() => setShowRender(false)}
        />
      )}
    </div>
  );
}
